// The Akaiva sign-in frame for the signed-out and account screens, shared with the admin console
// and the driver app (constitution 1.1.0; FR-027 exception): the backdrop; the Akaiva logo and a
// welcome line on the left; one card (max width 480) holding the screen on the right. Below the
// desktop breakpoint the parts stack, full width with 16 px margins. Scrolls when the keyboard or a
// small screen needs it. Controls inside take the admin console's sign-in look
// (src/ui/signInFrame.ts).
import { Image } from "expo-image";
import type { ReactNode } from "react";
import {
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from "react-native";

import { makeStyles } from "@/theme/makeStyles";
import { useTheme } from "@/theme/useTheme";
import { SignInFrameContext } from "@/ui/signInFrame";
import { Text } from "@/ui/Text";

import LOGO from "../../../assets/akaiva-loader-static.png";
import { AuthBackdrop } from "./AuthBackdrop";

/** The card's maximum width (the admin console's sign-in card). */
export const AUTH_FRAME_MAX_WIDTH = 480;

/** Logo artwork is 400 × 303. */
const LOGO_ASPECT = 400 / 303;

/** The welcome line beside the card. */
export type AuthIntro = { title: string; text: string };

const DEFAULT_INTRO: AuthIntro = {
  title: "Akaiva Edu Power",
  text: "School books and stationery, from your school's own booklist",
};

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    screen: { flex: 1, backgroundColor: t.color.scene },
    scroll: {
      flexGrow: 1,
      paddingHorizontal: t.space.lg,
      paddingTop: t.space.lg,
      paddingBottom: t.space.x3l,
      gap: t.space.x2l,
    },
    scrollWide: {
      flexDirection: "row",
      paddingHorizontal: t.space.x3l + t.space.lg,
      paddingVertical: t.space.x2l,
      gap: t.space.x3l + t.space.lg,
    },
    side: { gap: t.space.lg, alignItems: "flex-start" },
    sideWide: { flex: 1, gap: t.space.x3l },
    intro: { gap: t.space.sm },
    introWide: { paddingLeft: t.space.x3l + t.space.lg },
    center: { alignItems: "center" },
    centerWide: { flex: 1, justifyContent: "center" },
    card: {
      width: "100%",
      maxWidth: AUTH_FRAME_MAX_WIDTH,
      gap: t.space.x2l,
      borderWidth: t.borderWidth.card,
      borderColor: t.color.sceneEdge,
      borderRadius: t.radius.card,
      backgroundColor: t.color.sceneCard,
    },
  }),
);

export type AuthFrameProps = {
  children: ReactNode;
  /** The welcome line beside the card; a general welcome when left out. */
  intro?: AuthIntro;
  testID?: string;
};

export function AuthFrame({
  children,
  intro = DEFAULT_INTRO,
  testID,
}: AuthFrameProps) {
  const styles = useStyles();
  const theme = useTheme();
  const { width } = useWindowDimensions();
  const wide = width >= theme.breakpoint.desktop;
  const logoWidth = wide ? 168 : 120;
  return (
    <SignInFrameContext.Provider value={true}>
      <View style={styles.screen} testID={testID}>
        <AuthBackdrop />
        <ScrollView
          contentContainerStyle={[styles.scroll, wide && styles.scrollWide]}
          keyboardShouldPersistTaps="handled"
          role="main"
          testID={testID ? `${testID}-scroll` : undefined}
        >
          <View style={[styles.side, wide && styles.sideWide]}>
            <View role="banner">
              <Image
                source={LOGO}
                style={{ width: logoWidth, height: logoWidth / LOGO_ASPECT }}
                contentFit="contain"
                accessible
                accessibilityLabel="Akaiva"
                role="img"
              />
            </View>
            <View style={[styles.intro, wide && styles.introWide]}>
              <Text variant="heading" size={wide ? "x6l" : "x4l"}>
                {intro.title}
              </Text>
              <Text tone="muted" size={wide ? "xl" : "lg"}>
                {intro.text}
              </Text>
            </View>
          </View>
          <View style={[styles.center, wide && styles.centerWide]}>
            <View
              style={[
                styles.card,
                {
                  padding: wide
                    ? theme.space.x3l + theme.space.lg
                    : theme.space.x2l,
                },
              ]}
              testID={testID ? `${testID}-card` : undefined}
            >
              {children}
            </View>
          </View>
        </ScrollView>
      </View>
    </SignInFrameContext.Provider>
  );
}
